import {Dispatch, useCallback, useContext, useState} from "react";
import {ManifestFilterTypePayload} from "../../../providers/AssignmentViewReducer";
import ManifestDataProvider from "../providers/ManifestDataProvider";
import AssignDriverContainer from "./components/AssignDriverContainer";
import {ManifestFilterType} from "../list/components/manifest-filter/ManifestFilterState";
import {DateRange} from "@blueprintjs/datetime2";
import {ManifestDetailsAssignmentState, ManifestDetailsAssignmentActions} from "./ManifestDetailsReducer";
import {OptimizedStatusTypes, StatusType} from "../list/components/manifest-filter/ManifestFilter";
import {buildManifestStatusFilter, buildOptimizedStatusFilter} from "../../../views/UnassignedJobsView";
import ManifestDriverPanel from "../list/ManifestDriverPanel";
import {ManifestAttributeFilter, ManifestFilter} from "../../../generated/graphql";
import {hasSelectedDispatchStations, useDispatchStationContext} from "../../../providers/DispatchStationProvider";
import {useDispatchGroupContext} from "../../../providers/DispatchGroupProvider";
import {makeQueryStringFilter} from "../../../utils/FilterUtils";
import {useAssignmentViewState} from "../../../providers/AssignmentViewProvider";
import {useAssignmentState} from "../../../providers/AssignmentProdiver";
import {useFeatureFlagContext} from "../../../providers/FeatureFlagProvider";
import {extractSimplePref, PreferenceContext} from "../../../providers/PreferenceProvider";
import {TenantPreferences} from "../../../constants/constants";
import {buildManifestDriverGroupFilter} from "../../../utils/ManifestUtils";

type ManifestDetailsManifestSelectorProps = {
  manifestDetailsAssignmentState: ManifestDetailsAssignmentState;
  manifestDetailsAssignmentDispatch: Dispatch<ManifestDetailsAssignmentActions>;
};

const ManifestDetailsManifestSelector = ({
  manifestDetailsAssignmentState,
  manifestDetailsAssignmentDispatch
}: ManifestDetailsManifestSelectorProps) => {
  const {driverRequirements: driverRequirementsEnabled, skills: isSkillsEnabled} = useFeatureFlagContext();
  const {tenantPreferences} = useContext(PreferenceContext);

  const assignmentViewState = useAssignmentViewState();
  const {reassignJobsRequirements} = useAssignmentState();

  const dispatchStationState = useDispatchStationContext();
  const {driverGroups} = useDispatchGroupContext();

  const [statusTypes, setStatusTypes] = useState<StatusType[]>([]);
  const [selectedOptimizedStatusTypes, setSelectedOptimizedStatusTypes] = useState<OptimizedStatusTypes[]>([]);

  const isSkillsBasedRequirements = extractSimplePref(
    tenantPreferences,
    TenantPreferences.skillsBasedRequirements,
    false
  ).value as boolean;

  const onFilterSelected = useCallback(
    (filterState: ManifestFilterTypePayload[], oldFilterState?: ManifestFilterTypePayload[]) => {
      const payload = () => {
        let state = manifestDetailsAssignmentState.assignDriverManifestFilterState!;
        if (oldFilterState) {
          state = state.clearManifestFilterTypes(oldFilterState.map((f) => f.filterType));
        }
        if (filterState.length === 0) {
          state = state.addManifestFilterValue(undefined, ManifestFilterType.NONE);
        } else {
          state = state.addManifestFilterValues(filterState);
        }

        return state.copy();
      };
      manifestDetailsAssignmentDispatch({
        type: "SET_ASSIGNDRIVER_MANIFEST_FILTER_STATE",
        payload: payload()
      });
    },
    [manifestDetailsAssignmentDispatch, manifestDetailsAssignmentState.assignDriverManifestFilterState]
  );

  const onFilterDateChanged = useCallback(
    (dateRange: DateRange | undefined, selectedDateRange: DateRange | undefined) => {
      if (dateRange !== selectedDateRange) {
        const payload = () => {
          const state = manifestDetailsAssignmentState.assignDriverManifestFilterState!.replaceManifestFilterValue(
            dateRange,
            ManifestFilterType.DATE_RANGE
          );
          return state.copy();
        };
        manifestDetailsAssignmentDispatch({
          type: "SET_ASSIGNDRIVER_MANIFEST_FILTER_STATE",
          payload: payload()
        });
      }
    },
    [manifestDetailsAssignmentDispatch, manifestDetailsAssignmentState.assignDriverManifestFilterState]
  );

  const onSelectDriverGroup = useCallback(
    (key: number, arrayOfManifestFilter: React.MutableRefObject<number[]>) => {
      arrayOfManifestFilter.current.push(key);
      const payload = () => {
        const state = manifestDetailsAssignmentState.assignDriverManifestFilterState!.addManifestFilterValue(
          key,
          ManifestFilterType.DRIVER_GROUP
        );
        return state.copy();
      };
      manifestDetailsAssignmentDispatch({
        type: "SET_ASSIGNDRIVER_MANIFEST_FILTER_STATE",
        payload: payload()
      });
    },
    [manifestDetailsAssignmentDispatch, manifestDetailsAssignmentState.assignDriverManifestFilterState]
  );

  const onUnselectDriverGroup = useCallback(
    (key: number, arrayOfManifestFilter: React.MutableRefObject<number[]>) => {
      arrayOfManifestFilter.current = arrayOfManifestFilter.current.filter((d) => d !== key);

      const payload = () => {
        const state = manifestDetailsAssignmentState.assignDriverManifestFilterState!.removeManifestFilterValue(
          key,
          ManifestFilterType.DRIVER_GROUP
        );
        return state.copy();
      };
      manifestDetailsAssignmentDispatch({
        type: "SET_ASSIGNDRIVER_MANIFEST_FILTER_STATE",
        payload: payload()
      });
    },
    [manifestDetailsAssignmentDispatch, manifestDetailsAssignmentState.assignDriverManifestFilterState]
  );

  const onReplaceDriverGroup = useCallback(
    (arrayOfManifestFilter: React.MutableRefObject<number[]>) => {
      const payload = () => {
        const state = manifestDetailsAssignmentState.assignDriverManifestFilterState!.replaceManifestFilterValues(
          arrayOfManifestFilter.current,
          ManifestFilterType.DRIVER_GROUP
        );
        return state.copy();
      };
      manifestDetailsAssignmentDispatch({
        type: "SET_ASSIGNDRIVER_MANIFEST_FILTER_STATE",
        payload: payload()
      });
    },
    [manifestDetailsAssignmentDispatch, manifestDetailsAssignmentState.assignDriverManifestFilterState]
  );

  const onUnselectAllDriverGroup = useCallback(() => {
    const payload = () => {
      const state = manifestDetailsAssignmentState.assignDriverManifestFilterState!.clearManifestFilterType(
        ManifestFilterType.DRIVER_GROUP
      );
      return state.copy();
    };
    manifestDetailsAssignmentDispatch({
      type: "SET_ASSIGNDRIVER_MANIFEST_FILTER_STATE",
      payload: payload()
    });
  }, [manifestDetailsAssignmentDispatch, manifestDetailsAssignmentState.assignDriverManifestFilterState]);

  const onHandleManifestQuery = useCallback(
    (query: string) => {
      manifestDetailsAssignmentDispatch({type: "SET_ASSIGNDRIVER_MANIFEST_QUERY", payload: query});
    },
    [manifestDetailsAssignmentDispatch]
  );
  const buildFilter = useCallback(() => {
    const result: ManifestFilter = {
      manifest: {
        and: []
      }
    };
    buildManifestStatusFilter(result, statusTypes);
    buildOptimizedStatusFilter(result, selectedOptimizedStatusTypes);

    if (driverRequirementsEnabled && reassignJobsRequirements.size > 0) {
      const fieldRequirementsFilter =
        isSkillsBasedRequirements && isSkillsEnabled ? "driver_skills_id" : "driver_qualifications_id";
      result.manifest?.and?.push({
        and: [...reassignJobsRequirements.keys()].map((reqId) => {
          return {
            [fieldRequirementsFilter]: {term: reqId}
          };
        })
      });
    }
    if (hasSelectedDispatchStations(dispatchStationState)) {
      result.manifest?.and?.push(buildManifestDriverGroupFilter(driverGroups));
    }

    manifestDetailsAssignmentState.assignDriverManifestFilterState?.appendToFilter(result);

    if (manifestDetailsAssignmentState.assignDriverManifestQuery) {
      result.queryString = makeQueryStringFilter(manifestDetailsAssignmentState.assignDriverManifestQuery, [
        "driver.name",
        "driver.code",
        "manifestDriverId",
        "stops.job.jobNumber",
        "stops.job.routeNumber"
      ]);
    }
    return result;
  }, [
    statusTypes,
    selectedOptimizedStatusTypes,
    driverRequirementsEnabled,
    reassignJobsRequirements,
    manifestDetailsAssignmentState.assignDriverManifestFilterState,
    manifestDetailsAssignmentState.assignDriverManifestQuery,
    isSkillsBasedRequirements,
    isSkillsEnabled,
    driverGroups,
    dispatchStationState
  ]);

  return (
    <ManifestDataProvider
      filter={buildFilter()}
      isReassign={true}
      sort={{
        field: assignmentViewState.manifestSort.property,
        direction: assignmentViewState.manifestSort.sortDirection
      }}
    >
      <AssignDriverContainer>
        <ManifestDriverPanel
          statusTypes={statusTypes}
          setStatusTypes={setStatusTypes}
          selectedOptimizedStatusTypes={selectedOptimizedStatusTypes}
          setSelectedOptimizedStatusTypes={setSelectedOptimizedStatusTypes}
          hideCreateManifestButton={true}
          skipSavePreferences
          onManifestDetailsDrawerOpen={() => {
            //This is already in the drawer so we don't need to do anything
          }}
          filterSelected={onFilterSelected}
          filterDateChanged={onFilterDateChanged}
          selectDriverGroup={onSelectDriverGroup}
          unselectDriverGroup={onUnselectDriverGroup}
          replaceDriverGroup={onReplaceDriverGroup}
          unselectAllDriverGroup={onUnselectAllDriverGroup}
          handleManifestQuery={onHandleManifestQuery}
          disabledManifestIds={
            manifestDetailsAssignmentState.manifestId ? [manifestDetailsAssignmentState.manifestId] : []
          }
        />
      </AssignDriverContainer>
    </ManifestDataProvider>
  );
};

export default ManifestDetailsManifestSelector;
export type {ManifestDetailsManifestSelectorProps};
